import CheckFullIcon from '../../assets/images/Icons/Check/CheckFull';
import Radiobox from '../../assets/images/Icons/Radiobox';
import React, { PropsWithChildren } from 'react';
import Show from '../Show/Show';
import Tooltip from '../Tooltip/Tooltip';
import { Body1Reg } from '../../config/designSystem/typography';
import { COLORS } from '../../config/designSystem/Colors';
import { DropShadowMedium } from '../DropShadows/DropShadowMedium';
import { HORIZONTAL_MARGIN_DASHBOARD } from '../../constants/dashboardLayout';
import { Pressable } from 'react-native';
import { SPACING } from '../../config/designSystem/Spacing';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { observer } from 'mobx-react';
import { useAvailableDimensions } from '../../hooks/ui/useAvailableDimensions';

export type line = 'none' | 'start' | 'end' | 'both';
export interface ProgramBackgroundProps extends PropsWithChildren {
    done: boolean;
    onPress: () => void;
    line: line;
    marginBelow?: number;
    showOnboarding?: boolean;
    onboardingCb?: () => void;
    onboardingLabel?: string;
    onCloseComplete?: () => void;
}
export const SIZE_ICON_PROGRAM_CARD = SPACING['sp-24'];
export const COLUMNS_GAP_PROGRAM_CARD = SPACING['sp-12'];
export const PADDING_HORIZONTAL_PROGRAM_CARD = SPACING['sp-16'];

const _ProgramCardWrapper = ({children,
  done,
  onPress,
  line,
  marginBelow,
  showOnboarding = false,
  onboardingCb,
  onboardingLabel,
  onCloseComplete
}: ProgramBackgroundProps) => {

  const {width} = useAvailableDimensions();


  const WIDTH_REMAINING = width - 2*HORIZONTAL_MARGIN_DASHBOARD-COLUMNS_GAP_PROGRAM_CARD-SIZE_ICON_PROGRAM_CARD;
  // This is the dashed connector running between the session bullets, so only the LEFT
  // edge should be drawn. It used to set `borderWidth` (all four sides) with no width,
  // which on Android renders the whole rectangle -- its top edge showing up as a grey
  // bar sitting above the green check. borderLeftWidth draws just the vertical rule, and
  // width: 0 keeps the box from claiming horizontal space.
  const lineStyle = {
    position: 'absolute',
    borderStyle: 'dashed',
    borderLeftWidth: SPACING['sp-2'],
    borderColor: COLORS.surface.tertiary,
    width: 0,
    left: SIZE_ICON_PROGRAM_CARD/2 - 1,
  } as StyleProp<ViewStyle>;

  const renderMain = () => {
    return(<Tooltip
      from={(
        <Pressable onPress={() => {}}>
          <DropShadowMedium >
            <TouchableOpacity onPress={onPress} style={[{
              borderRadius: SPACING['sp-12'],
              paddingHorizontal: PADDING_HORIZONTAL_PROGRAM_CARD,
              paddingVertical: SPACING['sp-12'],
              rowGap: SPACING['sp-8'],
              backgroundColor: COLORS.surface['contrast'],
              width: WIDTH_REMAINING},
            done && {
              borderWidth: 1,
              borderColor: COLORS.state.success.primary,
            }]}>
              {children}
            </TouchableOpacity>
          </DropShadowMedium>
        </Pressable>)}
      isVisible={showOnboarding}
      placement={'bottom'}
      onClose={onboardingCb}
      onCloseComplete={onCloseComplete}
    >
      <Body1Reg>{onboardingLabel}</Body1Reg>
    </Tooltip>);

  };
  // The connector halves are positioned against this card's own box. The card
  // used to return a fragment, so they were positioned against the parent that
  // holds the WHOLE list: their percentages spanned every card and the line ran
  // on past the last bullet. The upper half stops at the bullet (the row centres
  // it); the lower half runs on through the gap below, down to the next card.
  const gapBelow = marginBelow || 0;
  const drawUpper = line === 'end' || line === 'both';
  const drawLower = line === 'start' || line === 'both';
  return(
    <View style={!!gapBelow && {marginBottom: gapBelow}}>
      <Show when={drawUpper}>
        <View style={[lineStyle, {top: 0, height: '50%'}]} />
      </Show>
      <Show when={drawLower}>
        <View style={[lineStyle, {top: '50%', bottom: -gapBelow}]} />
      </Show>
      <View style={{flexDirection:'row', columnGap: COLUMNS_GAP_PROGRAM_CARD, alignItems: 'center'}}>
        {/* Lift the bullet above the dashed connector.
            The connector Views are absolutely positioned and, despite being declared
            first, painted OVER the icon on Android -- so a grey dash crossed the green
            check at its top and bottom edge, reading as a bar sitting on the circle.
            zIndex covers iOS/web; elevation is what actually orders siblings on Android
            (it no longer paints a shadow, see config/designSystem/Shadow.ts). */}
        <View style={{zIndex: 1, elevation: 1}}>
          <Show when={done}>
            <CheckFullIcon width={SIZE_ICON_PROGRAM_CARD} height={SIZE_ICON_PROGRAM_CARD}
              bgColor={COLORS.state.success.primary}
              checkColor={COLORS.content['inverse-primary']}/>
          </Show>
          <Show when={!done}>
            <Radiobox size={SIZE_ICON_PROGRAM_CARD} />
          </Show>

        </View>
        {renderMain()}

      </View>
    </View>

  );

};

export const ProgramCardWrapper= observer(_ProgramCardWrapper);